/**
 * Chart notes layer. The post-it notes the skipper drops on the chart live
 * here, each drawn with the sticky-note icon and its optional short title.
 *
 * Like the marks and ownship layers, notes have to sit ABOVE the vector chart
 * cells. Those cells load asynchronously, and addCellToMap stacks their opaque
 * fills on top of whatever was added first, so a note dropped before the cells
 * finished loading would otherwise get buried under them (the same race the
 * ownship marker and marks hit). So after adding/updating we re-seat the note
 * layers on top, and MapView also calls raiseNotesLayers on a provider switch.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';
import type { ChartNote } from '@/store/notes';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';

const SOURCE_ID = 'notes';

/** The note layer ids, bottom -> top in draw order (and the re-seat order). */
const NOTES_LAYER_IDS = ['notes-icon', 'notes-label'] as const;

/** Lift the note layers to the top of the stack, in draw order. */
export function raiseNotesLayers(map: MaplibreMap): void {
  for (const id of NOTES_LAYER_IDS) {
    if (map.getLayer(id)) map.moveLayer(id);
  }
}

function build(notes: ChartNote[]): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: notes.map((n) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: n.position },
      properties: { kind: 'note', noteId: n.id, title: n.title ?? '' },
    })),
  };
}

export function ensureNotesLayer(map: MaplibreMap, notes: ChartNote[], palette: Palette): void {
  const p = getPalette(palette);
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: build(notes) });
  } else {
    (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(build(notes));
  }

  if (!map.getLayer('notes-icon')) {
    map.addLayer({
      id: 'notes-icon',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'note'],
      layout: {
        'icon-image': 'plot-note',
        'icon-size': 0.85,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });
  }
  if (!map.getLayer('notes-label')) {
    map.addLayer({
      id: 'notes-label',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'note'],
      layout: {
        'text-field': ['get', 'title'],
        'text-font': ['Open Sans Regular'],
        'text-size': 11,
        'text-offset': [0.9, 0],
        'text-anchor': 'left',
        'text-optional': true,
      },
      paint: { 'text-color': p.UI_ACCENT, 'text-halo-color': p.UI_BG, 'text-halo-width': 1.4 },
    });
  }

  // Keep dropped notes above the chart cells, whether created now or earlier (a
  // note placed before the async cells loaded would otherwise sit under them).
  raiseNotesLayers(map);
}

export function updateNotesLayer(map: MaplibreMap, notes: ChartNote[]): void {
  const src = map.getSource(SOURCE_ID);
  if (src) (src as maplibregl.GeoJSONSource).setData(build(notes));
}
